/**
 * NQIP Dashboard - Enterprise Grade
 * National Quality Indicators Program tracking and reporting
 */

import React, { useState, useEffect } from 'react';
import {
  BarChart3, TrendingUp, TrendingDown, Target, Award, Download,
  RefreshCw, FileText, AlertTriangle, CheckCircle, Calendar,
  ArrowRight, ArrowLeft, Filter, Search, Eye, Settings
} from 'lucide-react';
import { nqipData } from '../../data/mockData';
import { useAppContext } from '../../context/AppContext';

const NQIPDashboard = () => {
  const { state } = useAppContext();
  const selectedFacility = state.selectedFacility;
  
  const [data, setData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [selectedFacility, selectedPeriod]);

  const loadData = () => {
    setLoading(true);
    // In production, this would fetch from service
    const facilityData = nqipData.find(d => d.facilityId === selectedFacility) || nqipData[0];
    setData(facilityData);
    setLoading(false);
  };

  const handleExport = (format) => {
    const exportData = {
      facilityId: selectedFacility,
      period: data.period,
      data,
      exportedAt: new Date().toISOString()
    };

    if (format === 'csv') {
      const csv = generateCSV(exportData);
      downloadFile(csv, `nqip-${data.period}.csv`, 'text/csv');
    } else if (format === 'json') {
      const json = JSON.stringify(exportData, null, 2);
      downloadFile(json, `nqip-${data.period}.json`, 'application/json');
    }
    setShowExportModal(false);
  };

  const generateCSV = (data) => {
    const headers = ['Indicator', 'Rate', 'Target', 'Benchmark', 'Trend', 'Status'];
    const rows = Object.entries(data.data.indicators).map(([key, indicator]) => [
      key,
      indicator.rate,
      indicator.target,
      indicator.benchmark,
      indicator.trend,
      indicator.status
    ].join(','));
    
    return [headers.join(','), ...rows].join('\n');
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent': return 'bg-green-100 text-green-800 border-green-300';
      case 'good': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'satisfactory': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default: return 'bg-red-100 text-red-800 border-red-300';
    }
  };

  const getTrendIcon = (trend) => {
    if (trend < 0) {
      return <TrendingDown className="w-4 h-4 text-green-600" />;
    }
    return <TrendingUp className="w-4 h-4 text-red-600" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3 mb-2">
              <BarChart3 className="w-8 h-8" />
              National Quality Indicators Program (NQIP)
            </h1>
            <p className="text-blue-100 text-lg">Quarterly quality indicator reporting and compliance tracking</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowExportModal(true)}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg font-semibold flex items-center gap-2 transition-all"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={loadData}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg font-semibold flex items-center gap-2 transition-all"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Period Selector */}
        <div className="flex items-center gap-4 bg-white bg-opacity-10 rounded-lg p-4">
          <Calendar className="w-5 h-5" />
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 bg-white bg-opacity-20 rounded-lg text-white font-semibold border-none focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
          >
            <option value="current">Current Period: {data.period}</option>
            <option value="previous">Previous Period</option>
            <option value="year">Year to Date</option>
          </select>
          <div className="flex-1"></div>
          <div className="text-right">
            <div className="text-sm text-blue-100">Report Date</div>
            <div className="font-semibold">{new Date(data.reportDate).toLocaleDateString()}</div>
          </div>
        </div>
      </div>

      {/* Overall Score Card */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 shadow-lg border-2 border-blue-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-500 rounded-xl">
              <Award className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-blue-600">{data.overallScore}%</div>
              <div className="text-xs text-blue-500">Overall Score</div>
            </div>
          </div>
          <h3 className="font-bold text-blue-800 mb-2">NQIP Compliance</h3>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-sm text-green-700 font-semibold">Compliant</span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 shadow-lg border-2 border-green-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-500 rounded-xl">
              <Target className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-green-600">{data.benchmarks.national}%</div>
              <div className="text-xs text-green-500">National Avg</div>
            </div>
          </div>
          <h3 className="font-bold text-green-800 mb-2">National Benchmark</h3>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-green-600" />
            <span className="text-sm text-green-700 font-semibold">
              +{(data.overallScore - data.benchmarks.national).toFixed(1)}% above
            </span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 shadow-lg border-2 border-purple-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-500 rounded-xl">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-purple-600">{data.benchmarks.state}%</div>
              <div className="text-xs text-purple-500">State Avg</div>
            </div>
          </div>
          <h3 className="font-bold text-purple-800 mb-2">State Benchmark</h3>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-purple-600" />
            <span className="text-sm text-purple-700 font-semibold">
              +{(data.overallScore - data.benchmarks.state).toFixed(1)}% above
            </span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-6 shadow-lg border-2 border-indigo-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-indigo-500 rounded-xl">
              <Award className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-indigo-600">{data.benchmarks.peerGroup}%</div>
              <div className="text-xs text-indigo-500">Peer Group</div>
            </div>
          </div>
          <h3 className="font-bold text-indigo-800 mb-2">Peer Comparison</h3>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-indigo-600" />
            <span className="text-sm text-indigo-700 font-semibold">
              +{(data.overallScore - data.benchmarks.peerGroup).toFixed(1)}% above
            </span>
          </div>
        </div>
      </div>

      {/* Indicators Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(data.indicators).map(([key, indicator]) => {
          const isImproving = indicator.trend < 0;
          const performance = ((indicator.target - indicator.rate) / indicator.target) * 100;
          
          return (
            <div
              key={key}
              className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200 hover:border-blue-300 transition-all cursor-pointer"
              onClick={() => setSelectedIndicator({ key, ...indicator })}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-800 text-lg capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </h3>
                <span className={`px-3 py-1 text-xs rounded-full font-semibold border ${getStatusColor(indicator.status)}`}>
                  {indicator.status}
                </span>
              </div>

              <div className="space-y-3">
                <div className="flex items-end gap-2">
                  <div className="text-3xl font-bold text-gray-800">{indicator.rate}</div>
                  <div className="text-sm text-gray-500 mb-1">per 1,000 resident days</div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Target</span>
                    <span className="font-semibold">{indicator.target}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Benchmark</span>
                    <span className="font-semibold">{indicator.benchmark}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${isImproving ? 'bg-green-600' : 'bg-red-600'}`}
                      style={{ width: `${Math.min((indicator.rate / indicator.target) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                  <div className="flex items-center gap-2">
                    {getTrendIcon(indicator.trend)}
                    <span className={`text-sm font-semibold ${isImproving ? 'text-green-600' : 'text-red-600'}`}>
                      {Math.abs(indicator.trend)}% {isImproving ? 'improvement' : 'increase'}
                    </span>
                  </div>
                  <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all">
                    <Eye className="w-4 h-4" />
                  </button>
                </div>

                <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
                  <div className="flex justify-between">
                    <span>Incidents: {indicator.incidents}</span>
                    <span>Resident Days: {indicator.residentDays.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Trend Visualization */}
      <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-blue-600" />
          Performance Trends
        </h3>
        <div className="h-64 flex items-end justify-between gap-2">
          {Object.entries(data.indicators).map(([key, indicator]) => {
            const height = ((indicator.target - indicator.rate) / indicator.target) * 100;
            return (
              <div key={key} className="flex-1 flex flex-col items-center group">
                <div
                  className={`w-full rounded-t-lg transition-all cursor-pointer group-hover:opacity-80 ${
                    indicator.trend < 0 ? 'bg-gradient-to-t from-green-500 to-green-400' : 'bg-gradient-to-t from-red-500 to-red-400'
                  }`}
                  style={{ height: `${Math.max(height, 10)}%` }}
                  title={`${key}: ${indicator.rate} (Target: ${indicator.target})`}
                ></div>
                <div className="text-xs text-gray-600 mt-2 text-center">
                  {key.substring(0, 4)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Indicator Detail Modal */}
      {selectedIndicator && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 capitalize">
                {selectedIndicator.key.replace(/([A-Z])/g, ' $1').trim()} - Detailed View
              </h2>
              <button
                onClick={() => setSelectedIndicator(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Current Rate</div>
                  <div className="text-2xl font-bold text-blue-600">{selectedIndicator.rate}</div>
                  <div className="text-xs text-gray-500">per 1,000 resident days</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Target</div>
                  <div className="text-2xl font-bold text-green-600">{selectedIndicator.target}</div>
                  <div className="text-xs text-gray-500">Target rate</div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">Statistics</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Total Incidents:</span>
                    <span className="font-semibold ml-2">{selectedIndicator.incidents}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Resident Days:</span>
                    <span className="font-semibold ml-2">{selectedIndicator.residentDays.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Last Period:</span>
                    <span className="font-semibold ml-2">{selectedIndicator.lastPeriod}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Trend:</span>
                    <span className={`font-semibold ml-2 ${selectedIndicator.trend < 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {Math.abs(selectedIndicator.trend)}% {selectedIndicator.trend < 0 ? 'improvement' : 'increase'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <Download className="w-6 h-6 text-blue-600" />
                Export NQIP Data
              </h2>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => handleExport('csv')}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold flex items-center justify-center gap-2 transition-all"
              >
                <FileText className="w-5 h-5" />
                Export as CSV
              </button>
              <button
                onClick={() => handleExport('json')}
                className="w-full px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold flex items-center justify-center gap-2 transition-all"
              >
                <FileText className="w-5 h-5" />
                Export as JSON
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NQIPDashboard;
